import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { DraftWithContext, Message, QualificationSignal } from "../lib/types";
import { LeadStatusPill, DraftStatusPill, EyebrowPill } from "../components/StatusPill";
import { ConfidenceBadge, LowConfidenceBanner } from "../components/ConfidenceBadge";
import { ConversationStepper } from "../components/ConversationStepper";
import { ChatBubble } from "../components/ChatBubble";

export default function Thread() {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<DraftWithContext | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [signals, setSignals] = useState<QualificationSignal[]>([]);
  const [editedBubbles, setEditedBubbles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    const { data: d } = await supabase
      .from("drafts")
      .select(`*, conversations (*, leads (*))`)
      .eq("id", draftId)
      .single();
    if (!d) {
      setLoading(false);
      return;
    }
    setDraft(d as DraftWithContext);
    setEditedBubbles((d.draft_bubbles as string[]) ?? []);

    const [{ data: msgs }, { data: sigs }] = await Promise.all([
      supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", d.conversation_id)
        .order("sent_at", { ascending: true }),
      supabase
        .from("qualification_signals")
        .select("*")
        .eq("lead_id", (d as DraftWithContext).conversations.leads.id)
        .order("detected_at", { ascending: false }),
    ]);
    setMessages((msgs ?? []) as Message[]);
    setSignals((sigs ?? []) as QualificationSignal[]);
    setLoading(false);
  }

  useEffect(() => {
    if (draftId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  if (loading) return <div className="p-8 text-text-muted">Loading...</div>;
  if (!draft)
    return (
      <div className="flex h-full items-center justify-center text-text-muted">
        Draft introuvable.
      </div>
    );

  const lead = draft.conversations?.leads;
  const wasModified =
    JSON.stringify(editedBubbles) !== JSON.stringify(draft.draft_bubbles);
  const confidence = (draft as unknown as { confidence?: number }).confidence;
  const nextStep = (draft as unknown as { next_expected_step?: string }).next_expected_step;
  const isPending = draft.status === "pending";

  async function performAction(action: "send" | "edit" | "skip" | "disqualify" | "ignore") {
    if (acting) return; // double-click guard at the UI level too
    setActing(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/console-actions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              (await supabase.auth.getSession()).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            draftId: draft!.id,
            action,
            bubbles: action === "send" || action === "edit" ? editedBubbles : undefined,
            wasModified: action === "edit" ? wasModified : false,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      navigate("/");
    } catch (e) {
      alert(`Action failed: ${(e as Error).message}`);
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="grid h-full grid-cols-[minmax(0,1fr)_360px]">
      {/* Center pane */}
      <div className="flex flex-col overflow-hidden">
        {/* Stepper */}
        <div className="border-b border-border bg-bg-elevated/40 px-6 py-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <Link
              to="/"
              className="text-xs text-text-muted hover:text-text"
            >
              ← Inbox
            </Link>
            {nextStep && (
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-text-dim">Next:</span>
                <span className="rounded bg-bg-elevated px-2 py-0.5 font-mono text-text-muted">
                  {nextStep}
                </span>
              </div>
            )}
          </div>
          <ConversationStepper currentStage={nextStep} />
        </div>

        {/* Conversation */}
        <div className="flex-1 overflow-auto px-6 py-5">
          <ul className="space-y-4">
            {messages.map((m) => (
              <li key={m.id}>
                <ChatBubble message={m} />
              </li>
            ))}
          </ul>
        </div>

        {/* Draft editor */}
        <div className="border-t border-border bg-bg-elevated/60 p-4 backdrop-blur">
          {!isPending && (
            <div className="mb-2">
              <DraftStatusPill status={draft.status} />
            </div>
          )}
          {confidence !== undefined && confidence !== null && confidence < 0.55 && isPending && (
            <div className="mb-3">
              <LowConfidenceBanner confidence={confidence} />
            </div>
          )}
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <EyebrowPill>Draft Charles</EyebrowPill>
              {wasModified && (
                <span className="text-[11px] text-warn">✏️ édité</span>
              )}
            </div>
            <ConfidenceBadge confidence={confidence} />
          </div>

          <div className="space-y-2">
            {editedBubbles.map((b, i) => (
              <textarea
                key={i}
                value={b}
                onChange={(e) => {
                  const next = [...editedBubbles];
                  next[i] = e.target.value;
                  setEditedBubbles(next);
                }}
                rows={Math.max(2, b.split("\n").length + 1)}
                className="w-full resize-y rounded-lg border border-border bg-bg px-3.5 py-2.5 text-sm text-text focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/30 disabled:opacity-60"
                placeholder={`Bulle ${i + 1}`}
                disabled={!isPending}
              />
            ))}
          </div>

          {isPending && (
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => performAction(wasModified ? "edit" : "send")}
                disabled={acting}
                className="rounded-lg bg-accent-500 px-4 py-2 text-sm font-semibold text-white shadow-glow transition-opacity hover:bg-accent-600 disabled:opacity-50"
              >
                {acting ? "..." : wasModified ? "✏️ Send edited" : "✅ Send as-is"}
              </button>
              <button
                onClick={() => performAction("skip")}
                disabled={acting}
                className="rounded-lg border border-border px-4 py-2 text-sm text-text-muted hover:bg-bg-hover hover:text-text disabled:opacity-50"
              >
                ⏭ Skip
              </button>
              <button
                onClick={() => performAction("disqualify")}
                disabled={acting}
                className="rounded-lg border border-status-disqualified/40 px-4 py-2 text-sm text-status-disqualified hover:bg-status-disqualified/10 disabled:opacity-50"
              >
                🚫 Disqualify
              </button>
              <button
                onClick={() => {
                  if (
                    confirm(
                      "Marquer comme PAS un lead? L'agent ne draftra plus pour ce contact (team member, ami, spam, etc.)",
                    )
                  ) {
                    performAction("ignore");
                  }
                }}
                disabled={acting}
                className="ml-auto rounded-lg border border-border px-4 py-2 text-sm text-text-dim hover:bg-bg-hover hover:text-text-muted disabled:opacity-50"
                title="Pas un lead — ignore forever"
              >
                🗑️ Pas un lead
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right sidebar */}
      <aside className="overflow-auto border-l border-border bg-bg-elevated/30 p-5">
        <div className="mb-5">
          <EyebrowPill>Lead</EyebrowPill>
          <div className="mt-2 font-display text-lg font-semibold tracking-display text-text">
            {lead?.full_name ?? "(unknown)"}
          </div>
          {lead && (
            <div className="mt-1.5">
              <LeadStatusPill status={lead.current_status} />
            </div>
          )}
        </div>

        <div className="mb-5 space-y-2 text-sm">
          {lead?.industry && (
            <Field label="Industry" value={lead.industry} />
          )}
          {lead?.business_name && (
            <Field label="Business" value={lead.business_name} />
          )}
          {lead?.business_revenue_estimate && (
            <Field label="Revenu" value={lead.business_revenue_estimate} />
          )}
          {lead?.team_size_estimate !== null && lead?.team_size_estimate !== undefined && (
            <Field label="Team" value={`${lead.team_size_estimate} pers`} />
          )}
          {lead?.years_in_business !== null && lead?.years_in_business !== undefined && (
            <Field label="Années" value={`${lead.years_in_business} an${lead.years_in_business > 1 ? "s" : ""}`} />
          )}
        </div>

        {draft.reasoning && (
          <div className="mb-5">
            <EyebrowPill>Reasoning</EyebrowPill>
            <p className="mt-2 rounded-lg bg-bg-panel p-3 text-sm italic leading-relaxed text-text-muted ring-1 ring-border">
              {draft.reasoning}
            </p>
          </div>
        )}

        {signals.length > 0 && (
          <div className="mb-5">
            <EyebrowPill>Signals ({signals.length})</EyebrowPill>
            <ul className="mt-2 space-y-1.5">
              {signals.slice(0, 12).map((s) => (
                <li
                  key={s.id}
                  className="rounded-lg bg-bg-panel p-2.5 text-xs ring-1 ring-border"
                >
                  <div className="font-mono text-[10px] text-accent-400">
                    {s.signal_type}
                  </div>
                  {s.signal_value && (
                    <div className="mt-0.5 text-text">{s.signal_value}</div>
                  )}
                  {s.raw_quote && (
                    <div className="mt-1 italic text-text-dim">"{s.raw_quote}"</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {draft.examples_used && draft.examples_used.length > 0 && (
          <div className="mb-5">
            <EyebrowPill>Few-shot</EyebrowPill>
            <p className="mt-2 text-xs text-text-muted">
              {draft.examples_used.length} exemple(s) similaire(s) injecté(s) dans le prompt.
            </p>
          </div>
        )}

        {draft.rules_applied && draft.rules_applied.length > 0 && (
          <div>
            <EyebrowPill>Sanitizer</EyebrowPill>
            <ul className="mt-2 space-y-1 text-xs text-warn">
              {draft.rules_applied.map((r, i) => (
                <li key={i}>· {r}</li>
              ))}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-16 shrink-0 text-[11px] uppercase tracking-wider text-text-dim">
        {label}
      </span>
      <span className="text-text">{value}</span>
    </div>
  );
}
